import React, { useEffect, useCallback } from 'react';
import AppleLogin from 'react-apple-login';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

function decodeJwt(idToken) {
  try {
    // Split JWT into parts
    const parts = idToken.split('.');
    if (parts.length !== 3) return null;

    // Get payload (second part)
    const payloadB64 = parts[1];

    // Handle URL-safe base64: replace - with + and _ with /
    const normalized = payloadB64.replace(/-/g, '+').replace(/_/g, '/');

    // Decode base64
    const decoded = atob(normalized);

    // Parse JSON
    const payload = JSON.parse(decoded);

    return {
      sub: payload.sub || null,
      email: payload.email || null,
      name: payload.name || null
    };
  } catch (err) {
    console.error('Failed to decode JWT:', err);
    return null;
  }
}

export default function LoginScreen({ onLogin }) {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const handleSuccess = useCallback(async (data) => {
    try {
      // Extract id_token from Apple response
      const idToken = data?.authorization?.id_token;
      if (!idToken) {
        alert('Não foi possível obter token de autenticação');
        return;
      }

      // Extract user name (only provided on first login)
      let userName = null;
      if (data?.user?.name) {
        const { firstName, lastName } = data.user.name;
        userName = [firstName, lastName].filter(Boolean).join(' ');
      }

      // Call login API
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'apple',
          idToken,
          name: userName,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Erro no login: ${error.error || 'Tente novamente'}`);
        return;
      }

      const result = await response.json();

      // Call onLogin with token and user data
      onLogin(result.token, result.user);
    } catch (error) {
      console.error('Apple login error:', error);
      alert('Falha ao fazer login com Apple. Tente novamente.');
    }
  }, [onLogin, API_URL]);

  const handleError = useCallback((err) => {
    // Don't show error if user cancelled the sign-in
    // ERR_CANCELED or ERR_REQUEST_CANCELED are expected when user dismisses dialog
    const errorCode = err?.code || err?.error;
    if (errorCode === 'ERR_CANCELED' || errorCode === 'ERR_REQUEST_CANCELED' || errorCode === 'user_cancelled_authorize') {
      return; // Silent - this is expected behavior
    }

    console.error('Apple login error:', err);
    alert('Falha ao fazer login com Apple. Tente novamente.');
  }, []);

  // Google login handlers
  const handleGoogleSuccess = useCallback(async (credentialResponse) => {
    try {
      if (!credentialResponse?.credential) {
        alert('Falha ao obter credenciais do Google');
        return;
      }

      const idToken = credentialResponse.credential;

      // Call login API
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'google',
          idToken,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Erro no login: ${error.error || 'Tente novamente'}`);
        return;
      }

      const result = await response.json();

      // Call onLogin with token and user data
      onLogin(result.token, result.user);
    } catch (error) {
      console.error('Google login error:', error);
      alert('Falha ao fazer login com Google. Tente novamente.');
    }
  }, [onLogin, API_URL]);

  const handleGoogleError = useCallback(() => {
    console.error('Google login failed');
    alert('Falha ao fazer login com Google. Tente novamente.');
  }, []);

  useEffect(() => {
    // Track if we're already processing to prevent duplicate calls
    let isProcessing = false;

    // SDK event listeners connect to our handleSuccess/handleError callbacks
    const onSuccess = (event) => {
      // Prevent duplicate processing (SDK sometimes fires twice)
      if (isProcessing) return;

      isProcessing = true;
      handleSuccess(event.detail);
    };

    const onFailure = (event) => {
      if (isProcessing) return;

      isProcessing = true;
      handleError(event.detail.error);

      // Reset flag after error (user can try again)
      setTimeout(() => { isProcessing = false; }, 1000);
    };

    document.addEventListener('AppleIDSignInOnSuccess', onSuccess);
    document.addEventListener('AppleIDSignInOnFailure', onFailure);

    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', onSuccess);
      document.removeEventListener('AppleIDSignInOnFailure', onFailure);
    };
  }, [handleSuccess, handleError]);

  const handleGuestMode = () => {
    // Use a special guest ID format
    const guestId = 'guest:local';
    onLogin(guestId);
  };

  const handleDevLogin = () => {
    // Development-only: use dev user ID from environment
    const devUserId = process.env.REACT_APP_DEV_USER_ID;
    if (devUserId) {
      // Send dev user metadata
      onLogin(devUserId, { name: 'Dev User', email: 'dev@extrato.app' });
    }
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0fb662 0%, #0a8f4d 100%)',
        fontFamily: 'system-ui, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: 400,
          width: '100%',
          background: 'white',
          borderRadius: '20px',
          padding: '3rem 2rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <img
            src="/logo.png"
            alt="Extrato logo"
            style={{
              width: '80px',
              height: '80px',
              marginBottom: '1rem',
              objectFit: 'contain'
            }}
          />
          <h1 style={{
            marginBottom: '2.5rem',
            fontSize: '2.5rem',
            color: '#0fb662',
            fontWeight: 700
          }}>
            Extrato
            <span style={{ fontSize: '1.2rem' }}>🍅</span>
          </h1>

          {/* Google Sign-In Button */}
          {googleClientId && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                type="standard"
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
                width="280"
                locale="pt-BR"
              />
            </div>
          )}

          {/* Apple Sign-In Button */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div id="appleid-signin" style={{ width: '280px', height: '44px', margin: '0 auto' }}>
              <AppleLogin
                clientId={process.env.REACT_APP_APPLE_WEB_CLIENT_ID || 'com.aa2.notas.web'}
                redirectURI={window.location.origin}
                responseType="code id_token"
                responseMode="fragment"
                scope="name email"
                usePopup={true}
                callback={handleSuccess}
                onError={handleError}
              designProp={{
                height: 44,
                width: 280,
                color: 'black',
                border_radius: 12,
                type: 'sign-in',
                locale: 'pt_BR'
              }}
            />
          </div>
        </div>

        <div style={{
          margin: '2rem 0',
          borderTop: '1px solid #e0e0e0',
          position: 'relative'
        }}>
          <span style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            padding: '0 1rem',
            color: '#999',
            fontSize: '0.9rem'
          }}>
            ou
          </span>
        </div>

        <div>
          <button
            onClick={handleGuestMode}
            style={{
              width: '100%',
              maxWidth: 280,
              height: 44,
              border: '2px solid #0fb662',
              borderRadius: 12,
              background: 'white',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#0fb662',
              fontWeight: 600,
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#0fb662';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#0fb662';
            }}
          >
            Continuar como convidado
          </button>
          <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#999' }}>
            Dados salvos apenas neste dispositivo
          </p>
        </div>

        {process.env.REACT_APP_DEV_USER_ID && (
          <div style={{ marginTop: '1.5rem' }}>
            <button
              onClick={handleDevLogin}
              style={{
                width: '100%',
                maxWidth: 280,
                height: 44,
                border: '2px solid #ff6347',
                borderRadius: 12,
                background: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#ff6347',
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#ff6347';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#ff6347';
              }}
            >
              🔧 DEV: Login com Apple ID
            </button>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#999' }}>
              Apenas em desenvolvimento
            </p>
          </div>
        )}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}
